import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';

interface SupportFormData {
  fullName: string;
  age: string;
  gender: string;
  mobileNumber: string;
  whatsappNumber: string;
  emailAddress: string;
  address: string;
  supportType: string;
  otherSupportType: string;
  description: string;
  supportSince: string;
  isUrgent: string;
  employmentStatus: string;
  documents: FileList | null;
  declaration: boolean;
  consent: boolean;
}

const SupportRequest: React.FC = () => {
  const [formData, setFormData] = useState<SupportFormData>({
    fullName: '',
    age: '',
    gender: '',
    mobileNumber: '',
    whatsappNumber: '',
    emailAddress: '',
    address: '',
    supportType: '',
    otherSupportType: '',
    description: '',
    supportSince: '',
    isUrgent: '',
    employmentStatus: '',
    documents: null,
    declaration: false,
    consent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.age.trim()) newErrors.age = 'Age is required';
    else if (isNaN(Number(formData.age)) || Number(formData.age) < 0 || Number(formData.age) > 120) {
      newErrors.age = 'Please enter a valid age between 0 and 120';
    }
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.mobileNumber.trim()) newErrors.mobileNumber = 'Mobile number is required';
    if (!formData.whatsappNumber.trim()) newErrors.whatsappNumber = 'WhatsApp number is required';
    if (!formData.emailAddress.trim()) newErrors.emailAddress = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) newErrors.emailAddress = 'Please enter a valid email address';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.supportType) newErrors.supportType = 'Please select a type of support';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.isUrgent) newErrors.isUrgent = 'Please select if the request is urgent';
    if (!formData.employmentStatus) newErrors.employmentStatus = 'Employment status is required';
    if (!formData.documents || formData.documents.length === 0) newErrors.documents = 'Please upload relevant documents';
    if (!formData.declaration) newErrors.declaration = 'Please confirm the declaration';
    if (!formData.consent) newErrors.consent = 'Please provide consent to be contacted';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSupportTypeChange = (supportType: string) => {
    setFormData(prev => ({
      ...prev,
      supportType: supportType,
      // Clear otherSupportType if "Other" is being unselected
      otherSupportType: supportType !== 'Other' ? '' : prev.otherSupportType
    }));

    // Clear error when user selects an option
    if (errors.supportType) {
      setErrors(prev => ({ ...prev, supportType: '' }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setFormData(prev => ({
      ...prev,
      documents: files
    }));

    // Clear error when user uploads files
    if (errors.documents && files && files.length > 0) {
      setErrors(prev => ({ ...prev, documents: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for submission
      const formPayload = {
        fullName: formData.fullName,
        age: formData.age,
        gender: formData.gender,
        mobileNumber: formData.mobileNumber,
        whatsappNumber: formData.whatsappNumber,
        emailAddress: formData.emailAddress,
        address: formData.address,
        supportType: formData.supportType,
        otherSupportType: formData.otherSupportType,
        description: formData.description,
        supportSince: formData.supportSince,
        isUrgent: formData.isUrgent,
        employmentStatus: formData.employmentStatus,
        documentCount: formData.documents ? formData.documents.length : 0,
        declaration: formData.declaration ? 'Yes' : 'No',
        consent: formData.consent ? 'Yes' : 'No',
        submittedAt: new Date().toLocaleString(),
        _subject: `Support Request: ${formData.fullName} - ${formData.supportType}`
      };

      // Submit form using FormSubmit
      const response = await fetch('https://formsubmit.co/ajax/hello@mylvtrust.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formPayload)
      });

      const result = await response.json();

      if (result.success) {
        // Show success message
        setShowSuccess(true);

        // Reset form
        setFormData({
          fullName: '',
          age: '',
          gender: '',
          mobileNumber: '',
          whatsappNumber: '',
          emailAddress: '',
          address: '',
          supportType: '',
          otherSupportType: '',
          description: '',
          supportSince: '',
          isUrgent: '',
          employmentStatus: '',
          documents: null,
          declaration: false,
          consent: false
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Fallback to mailto if FormSubmit fails
      const emailSubject = `Support Request: ${formData.fullName} - ${formData.supportType}`;
      const emailBody = `SUPPORT REQUEST DETAILS

PERSONAL INFORMATION:
• Full Name: ${formData.fullName}
• Age: ${formData.age}
• Gender: ${formData.gender}
• Mobile Number: ${formData.mobileNumber}
• WhatsApp Number: ${formData.whatsappNumber}
• Email Address: ${formData.emailAddress}
• Address: ${formData.address}

TYPE OF SUPPORT REQUESTED:
• ${formData.supportType}
${formData.supportType === 'Other' && formData.otherSupportType ? `\n• Other Support Type: ${formData.otherSupportType}` : ''}

DETAILS OF REQUEST:
• Description: ${formData.description}
• Support Required Since: ${formData.supportSince}
• Is Urgent: ${formData.isUrgent}

EMPLOYMENT INFORMATION:
• Employment Status: ${formData.employmentStatus}

DECLARATION:
• Information True: ${formData.declaration ? 'Yes' : 'No'}
• Consent to Contact: ${formData.consent ? 'Yes' : 'No'}

Documents Uploaded: ${formData.documents ? formData.documents.length : 0} file(s)

---
Submitted on: ${new Date().toLocaleString()}`;

      const mailtoLink = `mailto:hello@mylvtrust.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.open(mailtoLink, '_blank');

      // Still show success message since we're falling back to mailto
      setShowSuccess(true);
      setFormData({
        fullName: '',
        age: '',
        gender: '',
        mobileNumber: '',
        whatsappNumber: '',
        emailAddress: '',
        address: '',
        supportType: '',
        otherSupportType: '',
        description: '',
        supportSince: '',
        isUrgent: '',
        employmentStatus: '',
        documents: null,
        declaration: false,
        consent: false
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You for Your Support Request</h1>
              <div className="text-left space-y-4 mb-8">
                <p className="text-gray-700">Thank you for submitting your support request to Our Trust.</p>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">What happens next:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700">
                    <li>Our team will carefully review your request and the information provided</li>
                    <li>We will contact you within 1-3 working days for verification or additional information if needed</li>
                    <li>Based on our available resources, we will determine how we can best support you</li>
                  </ol>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Important Notes:</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Please keep your phone available for calls from our team</li>
                    <li>Have your documents ready for verification</li>
                    <li>All information shared with our Trust is kept confidential</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Contact Information:</h3>
                  <p className="text-gray-700">If you need to reach us urgently:</p>
                  <div className="space-y-1 text-gray-700 mt-2">
                    <p><strong>Phone:</strong> 99420 19000</p>
                    <p><strong>Email:</strong> hello@mylvtrust.com</p>
                    <p><strong>Address:</strong> 58, 59 F-3 DJ Garden Extension, Ramamurthy Road, Selvapuram, Coimbatore-641026, TN <svg className="w-5 h-3 ml-1 inline" viewBox="0 0 24 16" fill="none">
                      <rect width="24" height="5.33" fill="#FF9933" />
                      <rect width="24" height="5.33" y="5.33" fill="white" />
                      <rect width="24" height="5.33" y="10.66" fill="#138808" />
                      <circle cx="12" cy="8" r="1.8" fill="#000080" />
                      <circle cx="12" cy="8" r="2.2" fill="none" stroke="#000080" strokeWidth="0.3" />
                      <path d="M12 6.5 L12.4 7.3 L13.2 7.3 L12.6 7.8 L12.8 8.6 L12 8.1 L11.2 8.6 L11.4 7.8 L10.8 7.3 L11.6 7.3 Z" fill="#000080" />
                    </svg>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-lg transition-all duration-300"
                >
                  Back to Home
                </Link>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all duration-300"
                >
                  Submit Another Request
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center text-primary hover:text-primary-dark mb-4 transition-colors">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">LV Trust - Support Request Form</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Thank you for reaching out to Lakshmi Venkatesan Charitable Trust. We are committed to supporting communities through education, healthcare, and welfare initiatives. Please fill out this form completely so we can better understand your needs and provide appropriate assistance.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
            {/* Section 1: Personal Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FaUsers className="mr-3 text-primary" />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your age"
                  />
                  {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your mobile number"
                  />
                  {errors.mobileNumber && <p className="text-red-500 text-sm mt-1">{errors.mobileNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your WhatsApp number"
                  />
                  {errors.whatsappNumber && <p className="text-red-500 text-sm mt-1">{errors.whatsappNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                  {errors.emailAddress && <p className="text-red-500 text-sm mt-1">{errors.emailAddress}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Please include your complete address with area, city, state, and PIN code"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>
              </div>
            </div>

            {/* Section 2: Type of Support Required */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Type of Support Required</h2>

              <div className="ml-6 pl-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type of Support Requested <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {['Education Support', 'Healthcare/Medical Support', 'Child Welfare Support', 'Women Welfare Support', 'Other'].map((type) => (
                    <label key={type} className="flex items-center">
                      <input
                        type="radio"
                        name="supportType"
                        value={type}
                        checked={formData.supportType === type}
                        onChange={() => handleSupportTypeChange(type)}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-3 text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>

                {/* Conditional text input for "Other" */}
                {formData.supportType === 'Other' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      name="otherSupportType"
                      value={formData.otherSupportType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Please specify the other reason"
                    />
                  </div>
                )}

                {errors.supportType && <p className="text-red-500 text-sm mt-1">{errors.supportType}</p>}
              </div>
            </div>

            {/* Section 3: Details of the Request */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Details of the Request
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description of the situation and support needed <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Please provide detailed information about your current situation and the specific support you are seeking"
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Since when support is required
                    </label>
                    <input
                      type="text"
                      name="supportSince"
                      value={formData.supportSince}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., From last 6 months, Since January 2024, etc."
                    />
                    {errors.supportSince && <p className="text-red-500 text-sm mt-1">{errors.supportSince}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Is the request urgent? <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="isUrgent"
                      value={formData.isUrgent}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                    {errors.isUrgent && <p className="text-red-500 text-sm mt-1">{errors.isUrgent}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Employment Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>Employment Information
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select employment status</option>
                  <option value="employed">Employed</option>
                  <option value="unemployed">Unemployed</option>
                  <option value="student">Student</option>
                </select>
                {errors.employmentStatus && <p className="text-red-500 text-sm mt-1">{errors.employmentStatus}</p>}
              </div>
            </div>

            {/* Section 5: Document Upload */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg> Document Upload
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload relevant documents <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-600 mb-2">ID proof / medical report / fee receipt</p>
                  <p className="text-sm text-gray-500 mb-4">Maximum file size: 10MB, Accepted formats: PDF, JPG, PNG</p>
                  <input
                    type="file"
                    name="documents"
                    onChange={handleFileChange}
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary-dark transition-colors"
                  >
                    Choose Files
                  </label>
                  {formData.documents && formData.documents.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      {formData.documents.length} file(s) selected
                    </p>
                  )}
                </div>
                {errors.documents && <p className="text-red-500 text-sm mt-1">{errors.documents}</p>}
              </div>
            </div>

            {/* Section 6: Declaration */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6"> Declaration</h2>

              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="declaration"
                    checked={formData.declaration}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary mt-1"
                  />
                  <span className="ml-3 text-gray-700">
                    I confirm that all information provided in this form is true and correct to the best of my knowledge <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.declaration && <p className="text-red-500 text-sm mt-1">{errors.declaration}</p>}

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary mt-1"
                  />
                  <span className="ml-3 text-gray-700">
                    I consent to be contacted by LV Trust for verification purposes or further communication regarding my support request <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.consent && <p className="text-red-500 text-sm mt-1">{errors.consent}</p>}

                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    <strong>Notice under DPDP Act, 2023:</strong> Your personal details, contact info, and uploaded verification documents are collected strictly for assessing and processing your support request. To learn about your data rights (access, correction, erasure, nomination) and retention periods, please read our{' '}
                    <Link to="/privacy-policy" className="text-primary font-semibold underline hover:text-primary-dark" target="_blank" rel="noopener noreferrer">
                      Privacy Policy &amp; DPDPA Notice
                    </Link>.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-105"
              >
                {isSubmitting ? 'Submitting...' : 'Request Support'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportRequest;
